import { FREE_CELL, PlanCell, sameWork, WorkCell, workCell } from "./plan-types";

/** Блок в координатах очереди (закреплённые дни в неё не входят) */
export interface QueueBlock {
    orderId: string;
    sectionId?: string;
    start: number;
    length: number;
    /**
     * Дни блока как есть. Блок склеен по проекту и разделу, а работы плана внутри
     * могут быть разные — при расталкивании каждый день едет со своей.
     */
    cells?: WorkCell[];
}

export interface Range {
    start: number;
    end: number;
}

/** Непрерывные отрезки одного проекта внутри очереди */
export function queueBlocks(cells: PlanCell[]): QueueBlock[] {
    const blocks: QueueBlock[] = [];
    let current: QueueBlock | null = null;

    cells.forEach((cell, index) => {
        if (cell.kind !== "work") {
            current = null;
            return;
        }

        if (current && sameWork(current, cell) && current.start + current.length === index) {
            current.length += 1;
            current.cells!.push(cell);
            return;
        }

        current = { orderId: cell.orderId, sectionId: cell.sectionId, start: index, length: 1, cells: [cell] };
        blocks.push(current);
    });

    return blocks;
}

/**
 * Разложить блоки вокруг «якоря» — диапазона, который занял место жёстко.
 *
 * По свободным дням якорь просто расходится, никого не трогая. Блок, на который
 * он наехал, уезжает вправо ровно настолько, чтобы освободить место; если за ним
 * вплотную стоит следующий, толкается и он — и так по цепочке.
 *
 * Возвращает null, если кому-то не хватило места до конца диапазона.
 */
export function pushRight(total: number, anchor: Range, others: QueueBlock[]): QueueBlock[] | null {
    if (anchor.start < 0 || anchor.end > total) {
        return null;
    }

    const occupied: Range[] = [anchor];
    const result: QueueBlock[] = [];

    const ordered = others.slice().sort((left, right) => left.start - right.start);

    for (const block of ordered) {
        let start = block.start;
        let shifted = true;

        while (shifted) {
            shifted = false;

            for (const range of occupied) {
                if (start < range.end && start + block.length > range.start) {
                    start = range.end;
                    shifted = true;
                }
            }
        }

        if (start + block.length > total) {
            return null;
        }

        occupied.push({ start, end: start + block.length });
        result.push({ ...block, start });
    }

    return result;
}

/** Собрать ячейки очереди из якоря и разложенных блоков */
export function renderQueue(
    total: number,
    anchorStart: number,
    anchorValues: PlanCell[],
    blocks: QueueBlock[]
): PlanCell[] {
    const cells: PlanCell[] = [];

    for (let i = 0; i < total; i++) {
        cells.push(FREE_CELL);
    }

    anchorValues.forEach((value, index) => {
        cells[anchorStart + index] = value;
    });

    blocks.forEach((block) => {
        for (let i = 0; i < block.length; i++) {
            cells[block.start + i] = block.cells ? block.cells[i] : workCell(block.orderId, block.sectionId);
        }
    });

    return cells;
}
